/**
 * Create a mission as a sequence, so the route, the people, the watched
 * areas and the baseline cannot be forgotten behind a name-only dialog.
 *
 * Completing a step advances to the next one. The operator can still walk
 * back, or skip optional work, but they do not have to remember to press
 * Continue after the thing they just did.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RouteIcon from '@mui/icons-material/Route';
import {
  Mission,
  MissionBaseline,
  createMission,
  getMissionBaseline,
  startMissionRun,
} from '../../services/missionApi';
import OwnKitReview from './OwnKitReview';
import { tabCounts } from '../routePlanner/dock/intel';
import { missionRouteNames } from '../routePlanner/io/missionRoute';
import { useMissionScan } from '../routePlanner/dock/useMissionScan';
import { fromLocalInput, toLocalInput } from '../routePlanner/eventWindow';
import { metalSx, type MetalFinish } from '../../theme/metalButtons';

const GOLD = '#f5b942';

const STEPS = [
  {
    id: 'name',
    label: 'Name',
    title: 'Name the mission',
    description:
      'Gives the exercise a name and states what the team is going out to answer. This creates the record everything else is saved against.',
  },
  {
    id: 'route',
    label: 'Route',
    title: 'Plan the route',
    description:
      'Draws the line the convoy will drive and the stops along it. Places, events, weather, and corridor scans run after this is saved — they need a line to ask about.',
  },
  {
    id: 'places',
    label: 'Places',
    title: 'Scan places',
    description:
      'Hospitals, fuel, police, and crossings beside the route, from OpenStreetMap. Set how far off the line to look. Used for planning, not scored as threats.',
  },
  {
    id: 'events',
    label: 'Events',
    title: 'Scan events',
    description:
      'Scheduled disruptions along the line — closures, races, construction. Set the time window and how far off the line to search.',
  },
  {
    id: 'weather',
    label: 'Weather',
    title: 'Scan weather',
    description:
      'Active National Weather Service alerts and conditions along the line. The same source the map overlays use — not a forecast model.',
  },
  {
    id: 'corridor',
    label: 'Corridor',
    title: 'Scan the corridor',
    description:
      'RF and other findings already on the books beside the line. Set the corridor width. This is the picture of the road before anyone rolls, not the live watchlist.',
  },
  {
    id: 'staff',
    label: 'People & places',
    title: 'Who goes, and what to watch',
    description:
      'Assigns the vehicles and phones that are the convoy. Watched areas log arrivals when those phones enter them. Assigning at least one entity moves you on; areas are optional.',
  },
  {
    id: 'baseline',
    label: 'Baseline',
    title: 'Own radios',
    description:
      'Start a scan and listen 60 seconds so the devices assigned to this mission can check in. Only what those scanners hear in that minute or the minute before can be marked ours. Skip and do it from Follows later if you want.',
  },
  {
    id: 'start',
    label: 'Go live',
    title: 'Start the exercise',
    description:
      'Go live enrols the convoy, starts the run, and turns on Sentinel updates. Rehearse does the same collection without paging anyone.',
  },
] as const;

type StepId = (typeof STEPS)[number]['id'];

const Action: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  accent?: boolean;
  hazard?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, accent, hazard, children }) => {
  const finish: MetalFinish = hazard ? 'hazard' : accent ? 'titanium' : 'gun';
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        ...metalSx(finish),
        height: 28,
        px: 1.25,
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: 'inherit',
        fontSize: '0.7rem',
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </Box>
  );
};

export interface MissionSetupWizardProps {
  open: boolean;
  packetId?: number;
  mission: Mission | null;
  assignedCount: number;
  watchedCount: number;
  busy: boolean;
  /** Planner / pickers sit on top; stay mounted so step state is not reset. */
  hidden?: boolean;
  onClose: () => void;
  onError: (message: string | null) => void;
  onCreated: (id: number) => Promise<void>;
  onPlanRoute: (mission: Mission) => void;
  onAssign: () => void;
  onWatchAreas: () => void;
  onFinished: (mission?: Mission) => void;
  /** Own-kit capture happened; the workspace line should reread. */
  onBaselineChanged?: () => void;
  onIntelSaved?: (missionId: number) => void;
  /** Edit walks the builder from the route. Setup resumes at the first unfinished step. */
  intent?: 'setup' | 'edit';
}

const MissionSetupWizard: React.FC<MissionSetupWizardProps> = ({
  open,
  packetId,
  mission,
  assignedCount,
  watchedCount,
  busy,
  hidden,
  onClose,
  onError,
  onCreated,
  onPlanRoute,
  onAssign,
  onWatchAreas,
  onFinished,
  onBaselineChanged,
  onIntelSaved,
  intent = 'setup',
}) => {
  const [step, setStep] = useState<StepId>('name');
  const [draftName, setDraftName] = useState('');
  const [draftObjective, setDraftObjective] = useState('');
  const [baseline, setBaseline] = useState<MissionBaseline | null>(null);

  const prevRoute = useRef(false);
  const prevPlaces = useRef(false);
  const prevEvents = useRef(false);
  const prevWeather = useRef(false);
  const prevCorridor = useRef(false);
  const prevAssigned = useRef(0);
  const prevCaptured = useRef(0);

  const scan = useMissionScan(mission, {
    onPersisted: onIntelSaved,
  });
  const counts = tabCounts(scan.intel);
  const hasPlaces = !!scan.intel.places.data;
  const hasEvents = !!scan.intel.events.data;
  const hasWeather = !!scan.intel.weather.data;
  const hasCorridor = !!scan.intel.corridor.data;

  useEffect(() => {
    if (!open) return;
    setStep(
      !mission ? 'name'
        : intent === 'edit' ? 'route'
          : firstIncomplete(mission, assignedCount),
    );
    if (!mission) {
      setDraftName('');
      setDraftObjective('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !mission) return;
    getMissionBaseline(mission.id).then(setBaseline).catch(() => setBaseline(null));
  }, [open, mission, step]);

  const index = STEPS.findIndex((s) => s.id === step);
  const current = STEPS[index];
  const hasRoute = !!mission?.route_geometry;
  const captured = baseline?.captured || baseline?.entries?.length || 0;
  const routeKm = mission?.route_stats?.distance != null
    ? `${(mission.route_stats.distance / 1000).toFixed(1)} km`
    : null;
  const routeNames = mission ? missionRouteNames(mission) : { primary: 'Primary', fallback: null };

  const go = (id: StepId) => setStep(id);

  // Snapshot "already done" when landing on a step so we only advance on a
  // completion that happens here, not on a resume that was already finished.
  useEffect(() => {
    prevRoute.current = hasRoute;
    prevPlaces.current = hasPlaces;
    prevEvents.current = hasEvents;
    prevWeather.current = hasWeather;
    prevCorridor.current = hasCorridor;
    prevAssigned.current = assignedCount;
    prevCaptured.current = captured;
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || step !== 'route') return;
    if (hasRoute && !prevRoute.current) go('places');
    prevRoute.current = hasRoute;
  }, [open, step, hasRoute]);

  useEffect(() => {
    if (!open || step !== 'places') return;
    if (hasPlaces && !prevPlaces.current && !scan.intel.places.loading) go('events');
    prevPlaces.current = hasPlaces;
  }, [open, step, hasPlaces, scan.intel.places.loading]);

  useEffect(() => {
    if (!open || step !== 'events') return;
    if (hasEvents && !prevEvents.current && !scan.intel.events.loading) go('weather');
    prevEvents.current = hasEvents;
  }, [open, step, hasEvents, scan.intel.events.loading]);

  useEffect(() => {
    if (!open || step !== 'weather') return;
    if (hasWeather && !prevWeather.current && !scan.intel.weather.loading) go('corridor');
    prevWeather.current = hasWeather;
  }, [open, step, hasWeather, scan.intel.weather.loading]);

  useEffect(() => {
    if (!open || step !== 'corridor') return;
    if (hasCorridor && !prevCorridor.current && !scan.intel.corridor.loading) go('staff');
    prevCorridor.current = hasCorridor;
  }, [open, step, hasCorridor, scan.intel.corridor.loading]);

  useEffect(() => {
    if (!open || step !== 'staff') return;
    if (assignedCount > 0 && prevAssigned.current === 0) go('baseline');
    prevAssigned.current = assignedCount;
  }, [open, step, assignedCount]);

  useEffect(() => {
    if (!open || step !== 'baseline') return;
    if (captured > 0 && prevCaptured.current === 0) go('start');
    prevCaptured.current = captured;
  }, [open, step, captured]);

  const create = async () => {
    if (!draftName.trim() || busy) return;
    onError(null);
    const created = await createMission({
      name: draftName.trim(),
      objective: draftObjective.trim() || undefined,
      packet_id: packetId,
    });
    await onCreated(created.id);
    go('route');
  };

  const live = async (kind: 'rehearsal' | 'live') => {
    if (!mission) return;
    onError(null);
    const next = await startMissionRun(mission.id, kind);
    onFinished(next);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      maxWidth="sm"
      fullWidth
      sx={hidden ? { visibility: 'hidden' } : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>
          {mission ? (intent === 'edit' ? 'Edit mission' : 'Set up mission') : 'New mission'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ px: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <Typography
              key={s.id}
              variant="caption"
              onClick={() => (mission || s.id === 'name') && go(s.id)}
              sx={{
                fontWeight: i === index ? 800 : 600,
                color: i === index ? GOLD : i < index ? 'text.primary' : 'text.disabled',
                cursor: mission || s.id === 'name' ? 'pointer' : 'default',
              }}
            >
              {i + 1}. {s.label}
            </Typography>
          ))}
        </Box>
        <LinearProgress
          variant="determinate"
          value={((index + 1) / STEPS.length) * 100}
          sx={{ mt: 0.75, height: 2, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
        />
      </Box>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {current.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {current.description}
        </Typography>

        {step === 'name' && (
          <>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Mission name"
              value={mission?.name || draftName}
              disabled={!!mission}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draftName.trim() && !mission) {
                  e.preventDefault();
                  create().catch((err: any) => onError(err?.response?.data?.detail || err?.message || 'Could not create the mission'));
                }
              }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label="Objective"
              placeholder="What the team is going out to answer"
              value={mission?.objective || draftObjective}
              disabled={!!mission}
              onChange={(e) => setDraftObjective(e.target.value)}
            />
          </>
        )}

        {step === 'route' && mission && (
          <>
            {hasRoute ? (
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: GOLD }}>
                {routeNames.primary} saved{routeKm ? ` · ${routeKm}` : ''}
                {mission.route_profile ? ` · ${mission.route_profile.replace('-', ' ')}` : ''}
                {(mission.saved_routes || []).filter((r) => r.id !== (mission.active_route_id || 'primary')).length
                  ? ` · ${(mission.saved_routes || []).filter((r) => r.id !== (mission.active_route_id || 'primary')).length} parked`
                  : ''}
                {mission.alt_route?.geometry
                  ? ` · fallback ${routeNames.fallback}${mission.alt_route.stats?.duration
                    ? ` · ${Math.round(mission.alt_route.stats.duration / 60)} min`
                    : ''}`
                  : ''}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                No route yet.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              In the left planner, name the live line. Add Secondary or Third
              with different stops or ends, then Use one to switch before or
              during the run. Fallback is a Mapbox option for the same stops.
            </Typography>
            <Action accent onClick={() => onPlanRoute(mission)}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                <RouteIcon sx={{ fontSize: 14 }} />
                {hasRoute
                  ? (mission.alt_route?.geometry ? 'Edit named routes' : 'Edit route · add fallback')
                  : 'Plan route'}
              </Box>
            </Action>
          </>
        )}

        {step === 'places' && mission && (
          <>
            <ScanRange
              label="Buffer"
              value={scan.placesRadiusM}
              min={250}
              max={5000}
              step={250}
              marks={[{ value: 250, label: '250 m' }, { value: 5000, label: '5 km' }]}
              onChange={scan.setPlacesRadiusM}
            />
            <ScanAction
              ready={hasRoute}
              loading={scan.intel.places.loading}
              error={scan.intel.places.error}
              summary={hasPlaces
                ? `${counts.places ?? 0} place${counts.places === 1 ? '' : 's'} within ${rangeLabel(scan.placesRadiusM)}`
                : `Not scanned yet · ${rangeLabel(scan.placesRadiusM)} either side`}
              label={hasPlaces ? 'Rescan places' : 'Scan places'}
              onScan={() => {
                scan.intel.places.setEnabled?.(true);
                scan.intel.places.refresh?.();
              }}
            />
          </>
        )}

        {step === 'events' && mission && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField
                label="From"
                type="datetime-local"
                size="small"
                value={toLocalInput(scan.eventsWindowStart)}
                onChange={(e) => {
                  const next = fromLocalInput(e.target.value);
                  if (next) scan.setEventsWindowStart(next);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="To"
                type="datetime-local"
                size="small"
                value={toLocalInput(scan.eventsWindowEnd)}
                onChange={(e) => {
                  const next = fromLocalInput(e.target.value);
                  if (next) scan.setEventsWindowEnd(next);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <ScanRange
              label="Search radius"
              value={scan.eventsCorridorM}
              min={250}
              max={5000}
              step={250}
              marks={[{ value: 250, label: '250 m' }, { value: 5000, label: '5 km' }]}
              onChange={scan.setEventsCorridorM}
            />
            <ScanAction
              ready={hasRoute}
              loading={scan.intel.events.loading}
              error={scan.intel.events.error}
              summary={hasEvents
                ? eventsScanSummary(scan.intel.events.data, counts.events)
                : `Not scanned yet · ${rangeLabel(scan.eventsCorridorM)} either side · next ${scan.eventsHorizonHours}h`}
              findings={eventFindings(scan.intel.events.data)}
              label={hasEvents ? 'Rescan events' : 'Scan events'}
              onScan={() => {
                scan.intel.events.setEnabled?.(true);
                scan.intel.events.refresh?.();
              }}
            />
          </>
        )}

        {step === 'weather' && mission && (
          <ScanAction
            ready={hasRoute}
            loading={scan.intel.weather.loading}
            error={scan.intel.weather.error}
            summary={hasWeather
              ? weatherScanSummary(scan.intel.weather.data, counts.weather)
              : 'Not scanned yet · NWS alerts and conditions along the route'}
            findings={weatherFindings(scan.intel.weather.data)}
            label={hasWeather ? 'Rescan weather' : 'Scan weather'}
            onScan={() => {
              scan.intel.weather.setEnabled?.(true);
              scan.intel.weather.refresh?.();
            }}
          />
        )}

        {step === 'corridor' && mission && (
          <>
            <ScanRange
              label="Corridor width"
              value={scan.corridorSettings.corridorM}
              min={50}
              max={1000}
              step={50}
              marks={[{ value: 50, label: '50 m' }, { value: 1000, label: '1 km' }]}
              onChange={(metres) => scan.setCorridorSettings({ corridorM: metres })}
            />
            <ScanAction
              ready={hasRoute}
              loading={scan.intel.corridor.loading}
              error={scan.intel.corridor.error}
              summary={hasCorridor
                ? `${counts.corridor ?? 0} hotspot${counts.corridor === 1 ? '' : 's'} within ${rangeLabel(scan.corridorSettings.corridorM)}`
                : `Not scanned yet · ${rangeLabel(scan.corridorSettings.corridorM)} either side`}
              label={hasCorridor ? 'Rescan corridor' : 'Scan corridor'}
              onScan={() => {
                scan.intel.corridor.setEnabled?.(true);
                scan.intel.corridor.refresh?.();
              }}
            />
          </>
        )}

        {step === 'staff' && mission && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">
                {assignedCount ? `${assignedCount} entit${assignedCount === 1 ? 'y' : 'ies'} assigned` : 'No entities assigned'}
              </Typography>
              <Action accent onClick={onAssign}>{assignedCount ? 'Change' : 'Assign'}</Action>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">
                {watchedCount ? `${watchedCount} area${watchedCount === 1 ? '' : 's'} watched` : 'No areas watched'}
              </Typography>
              <Action accent onClick={onWatchAreas}>{watchedCount ? 'Change' : 'Watch areas'}</Action>
            </Box>
          </>
        )}

        {step === 'baseline' && mission && (
          <>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              {captured
                ? `${captured} radio${captured === 1 ? '' : 's'} already trusted`
                : 'Nothing trusted yet.'}
            </Typography>
            <OwnKitReview
              missionId={mission.id}
              onError={onError}
              onChanged={() => {
                getMissionBaseline(mission.id).then(setBaseline).catch(() => setBaseline(null));
                onBaselineChanged?.();
              }}
            />
          </>
        )}

        {step === 'start' && mission && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.5 }}>
            <Check ok={hasRoute} label="Route planned" />
            <Check ok={hasPlaces} label="Places scanned" optional />
            <Check ok={hasEvents} label="Events scanned" optional />
            <Check ok={hasWeather} label="Weather scanned" optional />
            <Check ok={hasCorridor} label="Corridor scanned" optional />
            <Check ok={assignedCount > 0} label="Entities assigned" />
            <Check ok={captured > 0} label="Baseline captured" optional />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {index > 0 && <Action onClick={() => go(STEPS[index - 1].id)}>Back</Action>}
          {step === 'name' && !mission && (
            <Action accent disabled={!draftName.trim() || busy} onClick={() => create().catch((e: any) => onError(e?.response?.data?.detail || e?.message || 'Could not create the mission'))}>
              Continue
            </Action>
          )}
          {step === 'name' && mission && (
            <Action accent onClick={() => go('route')}>Continue</Action>
          )}
          {step === 'route' && (
            <Action accent disabled={!hasRoute} onClick={() => go('places')}>Continue</Action>
          )}
          {step === 'places' && (
            <Action accent onClick={() => go('events')}>{hasPlaces ? 'Continue' : 'Skip'}</Action>
          )}
          {step === 'events' && (
            <Action accent onClick={() => go('weather')}>{hasEvents ? 'Continue' : 'Skip'}</Action>
          )}
          {step === 'weather' && (
            <Action accent onClick={() => go('corridor')}>{hasWeather ? 'Continue' : 'Skip'}</Action>
          )}
          {step === 'corridor' && (
            <Action accent onClick={() => go('staff')}>{hasCorridor ? 'Continue' : 'Skip'}</Action>
          )}
          {step === 'staff' && (
            <Action accent onClick={() => go('baseline')}>{assignedCount ? 'Continue' : 'Skip'}</Action>
          )}
          {step === 'baseline' && (
            <Action accent onClick={() => go('start')}>{captured ? 'Continue' : 'Skip'}</Action>
          )}
          {step === 'start' && (
            <>
              <Action
                disabled={!hasRoute || busy}
                onClick={() => live('rehearsal').catch((e: any) => onError(e?.response?.data?.detail || e?.message || 'Could not start'))}
              >
                Baseline run
              </Action>
              <Action
                hazard
                disabled={!hasRoute || !assignedCount || busy}
                onClick={() => live('live').catch((e: any) => onError(e?.response?.data?.detail || e?.message || 'Could not go live'))}
              >
                Go live
              </Action>
            </>
          )}
          <Action onClick={onClose}>{step === 'start' ? 'Finish later' : 'Cancel'}</Action>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

function firstIncomplete(mission: Mission, assignedCount: number): StepId {
  if (!mission.route_geometry) return 'route';
  const intel = mission.route_intel || {};
  if (!intel.places) return 'places';
  if (!intel.events) return 'events';
  if (!intel.weather) return 'weather';
  if (!intel.corridor) return 'corridor';
  if (assignedCount === 0) return 'staff';
  return 'start';
}

function rangeLabel(metres: number): string {
  return metres >= 1000 ? `${(metres / 1000).toFixed(metres % 1000 === 0 ? 0 : 1)} km` : `${metres} m`;
}

const ScanRange: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  marks: Array<{ value: number; label: string }>;
  onChange: (metres: number) => void;
}> = ({ label, value, min, max, step, marks, onChange }) => (
  <Box sx={{ mb: 1.5, px: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
      {label} — {rangeLabel(value)} either side
    </Typography>
    <Slider
      size="small"
      value={value}
      min={min}
      max={max}
      step={step}
      marks={marks}
      valueLabelDisplay="auto"
      valueLabelFormat={rangeLabel}
      onChange={(_, next) => onChange(next as number)}
      sx={{ color: GOLD }}
    />
  </Box>
);

const ScanAction: React.FC<{
  ready: boolean;
  loading: boolean;
  error: string | null;
  summary: string;
  label: string;
  findings?: string[];
  onScan: () => void;
}> = ({ ready, loading, error, summary, label, findings, onScan }) => (
  <>
    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: error ? '#ef5350' : undefined }}>
      {error || summary}
    </Typography>
    {!error && findings && findings.length > 0 && (
      <Box sx={{ mb: 1 }}>
        {findings.map((line) => (
          <Typography key={line} variant="caption" sx={{ display: 'block', color: GOLD }}>
            {line}
          </Typography>
        ))}
      </Box>
    )}
    <Action accent disabled={!ready || loading} onClick={onScan}>
      {loading ? 'Scanning…' : label}
    </Action>
  </>
);

function eventsScanSummary(
  data: { events?: Array<{ title?: string }>; dataset?: { available?: boolean; note?: string | null } } | null,
  count: number | null,
): string {
  const note = data?.dataset?.note;
  if (note && data?.dataset?.available === false) return note;
  if (count) return `${count} event${count === 1 ? '' : 's'} you would drive through`;
  return note || 'No events in this window along the line';
}

function eventFindings(
  data: { events?: Array<{ title?: string; overlaps_transit?: boolean | null }> } | null,
): string[] {
  return (data?.events || [])
    .filter((event) => event.overlaps_transit !== false)
    .map((event) => event.title || '')
    .filter(Boolean)
    .slice(0, 4);
}

function weatherScanSummary(
  data: {
    conditions?: { shortForecast?: string; temperature?: number; temperatureUnit?: string } | null;
    alerts?: Array<{ event?: string }>;
  } | null,
  alertCount: number | null,
): string {
  const forecast = data?.conditions?.shortForecast;
  const temp = data?.conditions?.temperature;
  const unit = data?.conditions?.temperatureUnit || 'F';
  const bits: string[] = [];
  if (forecast) bits.push(temp != null ? `${forecast} · ${temp}°${unit}` : forecast);
  if (alertCount) bits.push(`${alertCount} alert${alertCount === 1 ? '' : 's'} in force`);
  else if (forecast) bits.push('No active alerts');
  return bits.join(' · ') || 'No active alerts on this line';
}

function weatherFindings(
  data: { alerts?: Array<{ event?: string; headline?: string }> } | null,
): string[] {
  return (data?.alerts || [])
    .map((alert) => alert.event || alert.headline || '')
    .filter(Boolean)
    .slice(0, 4);
}

const Check: React.FC<{ ok: boolean; label: string; optional?: boolean }> = ({ ok, label, optional }) => (
  <Typography variant="caption" sx={{ color: ok ? GOLD : 'text.secondary' }}>
    {ok ? '✓' : '○'} {label}{optional && !ok ? ' (optional)' : ''}
  </Typography>
);

export default MissionSetupWizard;
